import React, { useEffect, useState } from "react";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import Logger from "../../../services/Logger";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import {
    Alert,
    Box,
    Button,
    Checkbox,
    CircularProgress,
    FormControl,
    FormControlLabel,
    FormGroup,
    Grid,
    IconButton,
    InputAdornment,
    InputLabel,
    MenuItem,
    Paper,
    Select,
    TextField,
    ToggleButton,
    ToggleButtonGroup,
    Typography,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import ReactCountryFlag from "react-country-flag";
import CustomSnackbar from "../../components/CustomSnackbar";
import CustomTags from "../../components/CustomTags";
import GenreService from "../../setting/genre/GenreServie";
import { getContentLanguage } from "../../../services/LangPrefs";
import AdminGridService from "./AdminGridService";
import ApiService from "../../../services/ApiService";

/** Mini WORD_SEARCH sizes (rows×cols). Classic is always 12×12. */
const MINI_SIZES = [
    "5x5", "5x6",
    "6x6", "6x7", "6x8", "6x9",
    "7x7", "7x8", "7x9",
    "8x6", "8x8", "8x9",
    "9x9",
];

export default function GridCreateTemplate() {
    const navigate = useNavigate();
    const [title, setTitle] = useState("");
    const [content, setContent] = useState("");
    const [openSnackbar, setOpenSnackbar] = useState(false);
    const [severity, setSeverity] = useState("success");
    const [message, setMessage] = useState("");
    const [errorMessage, setErrorMessage] = useState("");
    const [genres, setGenres] = useState([]);
    const [selectedGenreId, setSelectedGenreId] = useState("");
    const [tags, setTags] = useState([]);
    const [tagsError, setTagsError] = useState(false);
    const [isKids, setIsKids] = useState(false);
    /** classic = 12×12 (may be Hidden Word); mini = WORD_SEARCH only. */
    const [sizeFamily, setSizeFamily] = useState("classic");
    const [miniSize, setMiniSize] = useState("9x9");
    const [isCreating, setIsCreating] = useState(false);
    const [loadingAssociations, setLoadingAssociations] = useState(false);

    useEffect(() => {
        const fetchGenres = async () => {
            try {
                const result = await GenreService.requestRootGenre();
                if (result && result.length > 0) {
                    setGenres(result);
                    // Set News as default (first item with name "News")
                    const newsGenre = result.find(genre => genre.name === "News");
                    if (newsGenre) {
                        setSelectedGenreId(newsGenre.id);
                    } else {
                        setSelectedGenreId(result[0].id);
                    }
                }
            } catch (error) {
                Logger.error("Error fetching genres:", error);
            }
        };
        fetchGenres();
    }, []);

    const handleCreate = async () => {
        setErrorMessage("");
        setTagsError(false);
        
        if (!title.trim() || !content.trim()) {
            handleSnackBar("error", "Please fill in both title and content");
            return;
        }

        if (!selectedGenreId) {
            handleSnackBar("error", "Please select a genre");
            return;
        }

        // Validate tags - at least 1 tag is required
        if (!tags || tags.length === 0) {
            handleSnackBar("error", "At least 1 tag is required. Please add at least one tag.");
            setTagsError(true);
            return;
        }

        setIsCreating(true);
        setErrorMessage(""); // Clear any previous error messages

        try {
            const language = getContentLanguage();
            const templateData = {
                title: title.trim(),
                content: content.trim(),
                genreId: selectedGenreId,
                language: language,
                isKids: isKids,
                size: sizeFamily === "mini" ? miniSize : "12x12",
            };
            
            // Convert tags to String[] (ensure all are strings)
            templateData.tags = tags.map(tag => String(tag).trim()).filter(tag => tag.length > 0);
            
            const result = await AdminGridService.createTemplate(templateData);

            // Check if the response indicates failure
            if (result && result.code === "FAILURE") {
                setErrorMessage(result.message || "Failed to create template");
                return;
            }

            handleSnackBar("success", "Template created successfully");

            // Navigate back to the template list after a short delay
            setTimeout(() => {
                navigate("/admin/puzzle/grid_tpl");
            }, 1500);
        } catch (error) {
            Logger.error("Error creating template:", error);
            setErrorMessage(error.message || "Error creating template");
        } finally {
            setIsCreating(false);
        }
    };

    const handleSnackBar = (severity, message) => {
        setOpenSnackbar(true);
        setSeverity(severity);
        setMessage(message);
    };

    const handleBack = () => {
        navigate("/admin/puzzle/grid_tpl");
    };

    const handleGetAssociations = async () => {
        if (!title.trim()) {
            handleSnackBar("error", "Please enter a title first");
            return;
        }

        setLoadingAssociations(true);
        setErrorMessage("");

        try {
            const language = getContentLanguage();
            const params = {
                title: title.trim(),
                language: language || 'english'
            };
            const result = await ApiService.getData("/cr/bedrock/word-associations", params);
            
            if (result && result.associations && Array.isArray(result.associations)) {
                // Join associations with commas for better readability
                const associationsText = result.associations.join(', ');
                setContent(associationsText);
                handleSnackBar("success", "Word associations loaded successfully");
            } else {
                handleSnackBar("error", "No associations found for this title");
            }
        } catch (error) {
            Logger.error("Error fetching word associations:", error);
            handleSnackBar("error", "Failed to fetch word associations. Please try again.");
        } finally {
            setLoadingAssociations(false);
        }
    };

    return (
        <Box sx={{ mb: 2 }}>
            <Grid container spacing={3}>
                <Grid size={{ xs: 12 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap' }}>
                        <Button
                            startIcon={<ArrowBackIcon />}
                            onClick={handleBack}
                            sx={{ mr: 2 }}
                        >
                            Back
                        </Button>
                        <Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                {(() => {
                                    const contentLang = getContentLanguage();
                                    const countryCode = contentLang === 'french' ? 'FR' : 'US';
                                    return (
                                        <ReactCountryFlag
                                            countryCode={countryCode}
                                            svg
                                            style={{
                                                width: '1.5em',
                                                height: '1.5em'
                                            }}
                                        />
                                    );
                                })()}
                                <Typography variant="h4" component="h1">
                                    Add New Grid Puzzle
                                </Typography>
                            </Box>
                            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                                This template can be used for both Word Search and Hidden Word puzzles
                            </Typography>
                        </Box>
                    </Box>
                </Grid>

                <Grid size={{ xs: 12 }}>
                    {errorMessage && (
                        <Alert 
                            severity="warning" 
                            onClose={() => setErrorMessage("")}
                            sx={{ mb: 2 }}
                        >
                            {errorMessage}
                        </Alert>
                    )}
                    <Grid container spacing={3}>
                        <Grid size={{ xs: 12 }}>
                            {/* Select a genre for the template */}
                            <Box sx={{
                                position: 'relative',
                                border: '1px solid #e0e0e0',
                                borderRadius: 1,
                                p: 2,
                                pt: 3
                            }}>
                                <Typography
                                    variant="caption"
                                    sx={{
                                        position: 'absolute',
                                        top: '-8px',
                                        left: '8px',
                                        backgroundColor: 'white',
                                        px: 1,
                                        color: 'text.secondary',
                                        fontSize: '0.75rem'
                                    }}
                                >
                                    Genre
                                </Typography>
                                <FormGroup row>
                                    {genres.map((genre) => (
                                        <FormControlLabel
                                            key={genre.id}
                                            control={
                                                <Checkbox
                                                    checked={selectedGenreId === genre.id}
                                                    onChange={() => setSelectedGenreId(genre.id)}
                                                    disabled={!genre.active}
                                                />
                                            }
                                            label={`${genre.name}${!genre.active ? ' (Inactive)' : ''}`}
                                            sx={{
                                                opacity: genre.active ? 1 : 0.6,
                                                mr: 2,
                                            }}
                                        />
                                    ))}
                                </FormGroup>
                            </Box>
                        </Grid>

                        <Grid size={{ xs: 12 }}>
                            <TextField
                                fullWidth
                                label="Title"
                                value={title}
                                onChange={(e) => setTitle(e.target.value)}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter' && !loadingAssociations && title.trim()) {
                                        e.preventDefault();
                                        handleGetAssociations();
                                    }
                                }}
                                placeholder="Enter a word or sentence"
                                variant="standard"
                                required
                                helperText="Enter a word or sentence. Click the icon or press Enter to call AI and the result will appear in the content field."
                                slotProps={{
                                    input: {
                                        endAdornment: (
                                            <InputAdornment position="end">
                                                <IconButton
                                                    onClick={handleGetAssociations}
                                                    disabled={loadingAssociations || !title.trim()}
                                                    color="primary"
                                                    aria-label="Get word associations"
                                                >
                                                    {loadingAssociations ? (
                                                        <CircularProgress size={20} />
                                                    ) : (
                                                        <AutoAwesomeIcon />
                                                    )}
                                                </IconButton>
                                            </InputAdornment>
                                        )
                                    }
                                }}
                            />
                        </Grid>

                        <Grid size={{ xs: 12 }}>
                            <TextField
                                fullWidth
                                label="Content"
                                value={content}
                                onChange={(e) => setContent(e.target.value)}
                                placeholder="Enter template content"
                                variant="standard"
                                multiline
                                rows={8}
                                required
                                helperText="Enter a substantial text passage. We'll extract words from it to create the puzzle. Short content won't provide enough words."
                            />
                        </Grid>

                        <Grid size={{ xs: 12 }}>
                            <CustomTags
                                value={tags}
                                onChange={(event, newTags) => {
                                    setTags(newTags);
                                    // Clear error when tags are added
                                    if (newTags.length > 0 && tagsError) {
                                        setTagsError(false);
                                    }
                                }}
                                options={[]}
                                label="Tags *"
                                variant="standard"
                                helperText={tagsError ? "At least 1 tag is required." : "At least 1 tag is required. Tags help categorize puzzles, e.g., book, comic, children."}
                                error={tagsError}
                            />
                        </Grid>

                        <Grid size={{ xs: 12 }}>
                            <Box sx={{ display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap" }}>
                                <Typography variant="body2" color="text.secondary">
                                    Board size
                                </Typography>
                                <ToggleButtonGroup
                                    value={sizeFamily}
                                    exclusive
                                    size="small"
                                    onChange={(_, next) => {
                                        if (next != null) setSizeFamily(next);
                                    }}
                                    aria-label="Board size family"
                                >
                                    <ToggleButton value="classic" sx={{ minHeight: 44 }}>
                                        Classic 12×12
                                    </ToggleButton>
                                    <ToggleButton value="mini" sx={{ minHeight: 44 }}>
                                        Mini (Word Search)
                                    </ToggleButton>
                                </ToggleButtonGroup>
                                {sizeFamily === "mini" ? (
                                    <FormControl size="small" sx={{ minWidth: 140 }}>
                                        <InputLabel id="mini-size-label">Mini size</InputLabel>
                                        <Select
                                            labelId="mini-size-label"
                                            label="Mini size"
                                            value={miniSize}
                                            onChange={(e) => setMiniSize(e.target.value)}
                                        >
                                            {MINI_SIZES.map((s) => (
                                                <MenuItem key={s} value={s}>
                                                    {s.replace("x", "×")}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                ) : null}
                            </Box>
                            <Typography variant="caption" color="text.secondary">
                                Classic 12×12 alternates between Word Search and Hidden Word. Mini always
                                creates a Word Search (Hidden Word stays 12×12).
                            </Typography>
                        </Grid>

                        <Grid size={{ xs: 12 }}>
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={isKids}
                                        onChange={(e) => setIsKids(e.target.checked)}
                                    />
                                }
                                label="Kids (if it's true, the AI also will generate easier words)"
                            />
                        </Grid>

                        <Grid size={{ xs: 12 }}>
                            <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                                <Button
                                    variant="contained"
                                    onClick={handleCreate}
                                    disabled={!title.trim() || !content.trim() || !selectedGenreId || !tags || tags.length === 0 || isCreating}
                                    size="large"
                                    startIcon={isCreating ? <CircularProgress size={20} color="inherit" /> : null}
                                >
                                    {isCreating ? "Creating..." : "Create Template"}
                                </Button>
                            </Box>
                        </Grid>
                    </Grid>
                </Grid>

                <Grid size={{ xs: 12 }}>
                    <Paper elevation={2} sx={{ p: 2, mt: 3, backgroundColor: '#e3f2fd', border: '1px solid #2196f3' }}>
                        <Typography variant="body2" color="primary" sx={{ fontWeight: 500 }}>
                            We'll extract nouns, adjectives, and verbs from your content and filter out banned words.
                            A template will be created in Draft version. You can add empty spaces in the 'HW Board' menu.
                        </Typography>
                    </Paper>
                </Grid>
            </Grid>

            <CustomSnackbar
                open={openSnackbar}
                setOpen={setOpenSnackbar}
                severity={severity}
                message={message}
            />
        </Box>
    );
}

